import { useLocation, Link } from "react-router-dom";
import {
  Palette, Wifi, CalendarDays, Eye, Send, SlidersHorizontal,
  Menu, CalendarPlus,
} from "lucide-react";
import { isAuthenticated, getStoredUser } from "@/lib/authToken";

// ── Die 6 Produkt-Loops (Kundenreise / „so funktioniert das System") ──────────
// Bewusst KEINE technischen Tabs, sondern eine geordnete Orientierungs-/Progress-
// Leiste. Jede Phase zeigt auf die ehrliche, bestehende Zielfläche (kein neuer
// Screen). Anker (#markenprofil / #kalender-bereich / #veroeffentlichen) werden von
// ScrollToHash (App.js) sanft angesprungen; fehlt ein Anker, bleibt nur der Sprung aus.
export const PRODUCT_LOOPS = [
  {
    key: "brand", label: "Markenprofil", short: "Marke",
    to: "/settings#markenprofil", icon: Palette,
    hint: "Wer bist du?",
    match: (p, h) => p.startsWith("/settings") && h === "#markenprofil",
  },
  {
    key: "channels", label: "Kanäle verbinden", short: "Kanäle",
    to: "/kanale", icon: Wifi,
    hint: "Wo posten?",
    match: (p) => p === "/kanale",
  },
  {
    key: "plan", label: "Monatsplan", short: "Plan",
    to: "/monthly-plan", icon: CalendarDays,
    hint: "Dein ganzer Monat",
    match: (p, h) => p === "/monthly-plan" && !h,
  },
  {
    // Zielt bewusst NICHT auf /calendar: die dortige Ansicht liest db.content und
    // zeigt Autopilot-/Monatsplan-Posts grundsätzlich nie — der Schritt wäre für
    // Kunden dauerhaft leer. Die vorbereiteten Beiträge stehen im Monatsplan.
    key: "review", label: "Beiträge ansehen", short: "Beiträge",
    to: "/monthly-plan#kalender-bereich", icon: Eye,
    hint: "Was ist geplant?",
    match: (p, h) => p === "/monthly-plan" && h === "#kalender-bereich",
  },
  {
    key: "publish", label: "Veröffentlichen", short: "Live",
    to: "/monthly-plan#veroeffentlichen", icon: Send,
    hint: "Live gehen",
    match: (p, h) => p === "/monthly-plan" && h === "#veroeffentlichen",
  },
  {
    key: "settings", label: "Daten & Einstellungen", short: "Daten",
    to: "/settings", icon: SlidersHorizontal,
    hint: "Konto & Datenschutz",
    match: (p, h) => p.startsWith("/settings") && h !== "#markenprofil",
  },
];

// ── Ein einzelner Schritt der Loop-Leiste ─────────────────────────────────────
const LoopStep = ({ loop, state, index }) => {
  const Icon = loop.icon;
  const isActive = state === "active";
  const isDone = state === "done";
  return (
    <Link
      to={loop.to}
      data-testid={`loop-${loop.key}`}
      aria-current={isActive ? "step" : undefined}
      className="group flex items-center gap-2.5 flex-shrink-0 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-stone-50"
      title={`${loop.label} — ${loop.hint}`}
    >
      <span
        className={`relative flex items-center justify-center w-8 h-8 rounded-full border transition-all ${
          isActive
            ? "bg-fox-fire border-fox-fire text-white shadow-sm shadow-orange-200"
            : isDone
              ? "bg-fox-tint border-fox-fire/40 text-fox-fire"
              : "bg-white border-stone-300 text-stone-400 group-hover:border-stone-400 group-hover:text-stone-500"
        }`}
      >
        <Icon size={16} strokeWidth={2.2} />
        <span
          className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center border border-white ${
            isActive ? "bg-brand-indigo text-white" : isDone ? "bg-fox-fire text-white" : "bg-stone-200 text-stone-500"
          }`}
        >
          {index + 1}
        </span>
      </span>
      <span className="flex flex-col leading-tight">
        <span
          className={`text-[13px] font-semibold whitespace-nowrap ${
            isActive ? "text-stone-900" : isDone ? "text-stone-700" : "text-stone-500 group-hover:text-stone-700"
          }`}
        >
          <span className="hidden xl:inline">{loop.label}</span>
          <span className="xl:hidden">{loop.short}</span>
        </span>
      </span>
    </Link>
  );
};

// ── Seiten-Kontext: je Bereich ein thematisch passender Header-Loop ───────────
// Ordnet jede Route ihrem Produktbereich zu (Reihenfolge = spezifisch → allgemein).
const SECTIONS = [
  [/^\/$/, "Übersicht"],
  [/^\/assistent/, "Start"],
  [/^\/kanale/, "Kanäle verbinden"],
  [/^\/(monthly-plan|calendar|campaigns|media|masterpost|generator|content-tools|social-media|instagram|video)/, "Inhalte & Plan"],
  [/^\/(analytics|strategie)/, "Auswertung & Strategie"],
  [/^\/link-in-bio/, "Link-in-Bio"],
  [/^\/(pricing|planfinder)/, "Preise & Pakete"],
  [/^\/kontakt/, "Kontakt & Beratung"],
  [/^\/(hilfe|setup-guide)/, "Hilfe & Setup"],
  [/^\/bonus/, "Bonus & Empfehlungen"],
  [/^\/geld-verdienen/, "Geld verdienen"],
  [/^\/(settings|datenschutz|impressum|nutzungsbedingungen|vertrauen|datenbasis|inbox|profile-optimizer|website-optimierung|moltbook)/, "Daten & Einstellungen"],
];

// ── Der globale Produkt-Header ────────────────────────────────────────────────
export default function ProductHeader({ onMenuClick }) {
  const { pathname, hash } = useLocation();
  const activeIndex = PRODUCT_LOOPS.findIndex((l) => l.match(pathname, hash));
  const sectionLabel = (SECTIONS.find(([re]) => re.test(pathname)) || ["", "Werkzeuge"])[1];

  const user = getStoredUser();
  const email = (isAuthenticated() && user?.email) || "";
  const initial = email ? email[0].toUpperCase() : "F";

  return (
    <header
      data-testid="product-header"
      className="fixed top-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-200"
    >
      {/* ── Zeile 1: Marke + Nutzer/CTA ── */}
      <div className="h-14 lg:h-16 px-4 md:px-6 flex items-center justify-between gap-3">
       <div className="flex items-center min-w-0">
        {/* Logo-Lockup (Maskottchen + Wortmarke, wie im Schwesterprodukt) */}
        <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="header-logo">
          <img
            src="/images/logo.png"
            alt="Förderfuchs"
            className="h-9 lg:h-10 w-auto flex-shrink-0"
            width="40"
            height="46"
          />
          <span className="flex flex-col leading-tight min-w-0">
            <span className="font-heading font-extrabold text-stone-900 text-[15px] lg:text-lg truncate">
              KI Social Media Manager
            </span>
            <span className="text-[11px] text-stone-500 font-medium tracking-wide">
              by Förderfuchs
            </span>
          </span>
        </Link>
        {/* Seiten-Kontext — je Bereich ein thematisch passender Loop */}
        <span data-testid="header-context" className="hidden xl:inline-flex items-center gap-2 pl-3 ml-3 border-l border-stone-200 text-sm font-medium text-stone-600 truncate max-w-[240px]">
          <span className="w-1.5 h-1.5 rounded-full bg-fox-fire flex-shrink-0" />
          {sectionLabel}
        </span>
       </div>

        {/* Rechts: Nutzer-Chip (Desktop) + Primär-CTA + Mobile-Menü */}
        <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
          <Link
            to="/monthly-plan"
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center gap-1.5 bg-fox-fire hover:bg-fox-fire-dark text-white text-sm font-semibold px-3.5 py-2 rounded-full transition-colors shadow-sm shadow-orange-100"
          >
            <CalendarPlus size={15} />
            <span className="hidden md:inline">Monatsplan</span>
            <span className="md:hidden">Plan</span>
          </Link>

          {email && (
            <div
              className="hidden lg:flex items-center gap-2 pl-1"
              title={`Angemeldet als ${email}`}
            >
              <span className="w-8 h-8 rounded-full brand-gradient text-white text-sm font-bold flex items-center justify-center">
                {initial}
              </span>
              <span className="hidden xl:block text-xs text-stone-500 max-w-[150px] truncate">
                {email}
              </span>
            </div>
          )}

          {/* Mobile-Menü öffnet die Sidebar (Werkzeuge) */}
          <button
            onClick={onMenuClick}
            data-testid="mobile-menu-btn"
            aria-label="Menü öffnen"
            className="lg:hidden p-2 -mr-1 rounded-lg text-stone-500 hover:bg-stone-100"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* ── Zeile 2: Loop-Stepper (die Orientierung / „so funktioniert es") ── */}
      <div className="h-[52px] lg:h-14 border-t border-stone-100 bg-gradient-to-b from-stone-50/60 to-white">
        <div className="h-full max-w-7xl mx-auto px-2 md:px-4 flex items-center overflow-x-auto loop-rail">
          {PRODUCT_LOOPS.map((loop, i) => {
            const state = i === activeIndex ? "active" : activeIndex > i ? "done" : "todo";
            return (
              <div key={loop.key} className="flex items-center flex-shrink-0">
                {i > 0 && (
                  <span
                    className={`w-4 md:w-8 h-px flex-shrink-0 transition-colors ${
                      activeIndex >= i ? "bg-fox-fire/50" : "bg-stone-200"
                    }`}
                  />
                )}
                <LoopStep loop={loop} state={state} index={i} />
              </div>
            );
          })}
        </div>
      </div>
    </header>
  );
}
